import { InvoiceProvider, useInvoice } from '../../common/InvoiceProvider';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Divider,
  TextField,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Spinner from '../../Spinner';
import intl from 'components/i18n/ReactIntlWrapper';
import PdfViewer from 'components/common/PdfRenderer';
import { useWorkflow, WorkflowProvider } from 'components/Workflow/WorkflowContext';
import { isWorkflowStateReadOnly } from 'components/Workflow/workflowUtils';
import AccountsPayableForm from './AccountsPayableForm';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import React, { useState, useCallback, useEffect } from 'react';

const InvoiceDetailAccountsPayableContent = ({ onlyView = false }) => {
  const {
    isBillFromPersonasLoading,
    setInput,
    input,
    isLoading,
    errors,
    setErrors,
  } = useInvoice();
  const api: Api = useApi();
  const { workflow } = useWorkflow(); 
  const [fromOptions, setFromOptions] = useState<{ id: number; name: string }[]>([]);
  const [fromLoading, setFromLoading] = useState(false);
  const [fromInputValue, setFromInputValue] = useState('');
  const [fromValue, setFromValue] = useState<{ id: number; name: string } | null>(null);

  const searchFrom = useCallback(async (term: string) => {
    setFromLoading(true);
    try {
      const res = await api.invoiceApi.apiVversionPersonaEligibleInvoiceFromTypeAheadGet('1', term);
      const results = (res.data?.data?.queryResult ?? [])
        .map((p) => ({ id: p.id, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setFromOptions(results);
    } finally {
      setFromLoading(false);
    }
  }, [api]);

  // Pre-populate options with the current persona so the Autocomplete shows it on load
  useEffect(() => {
    if (!input.idFrom) return;
    if (fromValue) return;
    const seed = (p: { id: number; name: string }) => {
      setFromOptions([p]);
      setFromValue(p);
      setFromInputValue(p.name);
    };
    if (input.from) {
      seed({ id: input.idFrom, name: input.from });
    } else {
      api.invoiceApi.apiVversionPersonaIdGet(input.idFrom, '1').then((res) => {
        const p = res.data?.data;
        if (p?.id && p?.name) seed({ id: p.id, name: p.name });
      });
    }
  }, [input.idFrom, input.from]);

  const handlePersonaChange = (persona: { id: number; name: string } | null) => {
    if (!persona) return;
    setFromValue(persona);
    setFromInputValue(persona.name);
    setInput((prev) => ({
      ...prev,
      idFrom: persona.id,
      from: persona.name,
    }));
    setErrors((prevErrors) => ({ ...prevErrors, idFrom: '' }));
  };

  return (
    <Box sx={{ mt: 4, px: 2 }}>
      <Card variant="outlined">
        {isLoading && <Spinner />}
        <CardHeader
          title={
            <Typography color="primary" variant="h2">
              Invoice Detail
            </Typography>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 4 }}>
              <Autocomplete
                options={fromOptions}
                getOptionLabel={(o) => o.name ?? ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                loading={fromLoading}
                disabled={isBillFromPersonasLoading || onlyView}
                value={fromValue}
                inputValue={fromInputValue}
                onInputChange={(_e, value, reason) => {
                  if (reason === 'input') {
                    setFromInputValue(value);
                    searchFrom(value);
                  } else if (reason === 'clear') {
                    setFromInputValue('');
                  }
                }}
                onChange={(_e, value) => handlePersonaChange(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={intl.get('newInvoice.invoiceSelectProfile')}
                    error={Boolean(errors?.idFrom) && errors?.idFrom !== 'missing confidence'}
                    helperText={errors?.idFrom && errors?.idFrom !== 'missing confidence' ? errors.idFrom : undefined}
                    onBlur={() => {}}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {input ? (
            <Grid container spacing={2} alignItems="flex-start" sx={{ width: '100%' }}>
              <Grid size={{ xs: 5, md: 5, sm: 5, lg: 5, xl: 5 }} sx={{ minWidth: 0 }}>
                {(input?.attachments || (input?.fileUri && input?.fileUri !== '')) && (
                  <Box mb={3}>
                    <PdfViewer
                      file={input['attachments']?.[0]}
                      dataURI={input['attachments']?.[0]?.dataUri}
                    />
                  </Box>
                )}
              </Grid>
              <Grid size={{ xs: 7, md: 7, sm: 7, lg: 7, xl: 7 }} sx={{ minWidth: 0 }}>
                <AccountsPayableForm onlyView={onlyView || isWorkflowStateReadOnly(workflow, input.idWorkflowState)} />
              </Grid>
            </Grid>
          ) : (
            <Spinner />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

const InvoiceDetailAccountsPayable = ( { onlyView = false } ) => {
  return (
    <InvoiceProvider review={true}>
      <WorkflowProvider moduleId={onlyView?4:5}>
        <InvoiceDetailAccountsPayableContent onlyView={onlyView} />
      </WorkflowProvider>
    </InvoiceProvider>
  );
};

export default InvoiceDetailAccountsPayable;
